import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { GitMerge } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { DuplicateGroup, DuplicateRecord, useMergeLeads } from '@/api/leads/use-leads';

export interface DuplicateMergeResult {
    survivor: DuplicateRecord;
    mergedIds: string[];
}

interface DuplicateGroupCardProps {
    group: DuplicateGroup;
    /** The record whose page the card is shown on, marked in the list. */
    currentId?: string;
    onMerged?: (result: DuplicateMergeResult) => void;
}

export const matchLabel = (matchedBy: DuplicateGroup['matched_by']) => {
    if (matchedBy.includes('email') && matchedBy.includes('phone')) return 'Same email or phone';
    return matchedBy.includes('email') ? 'Same email' : 'Same phone';
};

const errorMessage = (error: any) => {
    const errors = error.response?.data?.errors;
    return String(
        (errors && Object.values(errors).flat()[0]) ||
            error.response?.data?.message ||
            error.message ||
            'Failed to merge'
    );
};

const recordUrl = (record: DuplicateRecord) =>
    record.type === 'customer' ? `/customers/${record.id}` : `/leads/${record.id}`;

/**
 * Pick the record to keep: a customer when there is one (leads are always merged into
 * customers), else a lead with trading records (it cannot be merged away), else the
 * oldest. Only records the user may edit are considered.
 */
const defaultSurvivorId = (records: DuplicateRecord[]) => {
    const keepable = records.filter(record => record.can_keep);
    return (
        keepable.find(record => record.type === 'customer') ??
        keepable.find(record => record.has_financial_records) ??
        keepable[0]
    )?.id;
};

const DuplicateGroupCard: React.FC<DuplicateGroupCardProps> = ({ group, currentId, onMerged }) => {
    const { enqueueSnackbar } = useSnackbar();
    const mergeLeadsMutation = useMergeLeads();
    const recordIds = group.records.map(record => record.id).join(',');

    const [survivorId, setSurvivorId] = useState<string | undefined>(() =>
        defaultSurvivorId(group.records)
    );
    const [excluded, setExcluded] = useState<string[]>([]);
    const [confirming, setConfirming] = useState(false);

    useEffect(() => {
        setSurvivorId(defaultSurvivorId(group.records));
        setExcluded([]);
        setConfirming(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordIds]);

    const survivor = group.records.find(record => record.id === survivorId);
    const mergedIds = group.records
        .filter(
            record =>
                record.id !== survivorId && record.can_be_merged_in && !excluded.includes(record.id)
        )
        .map(record => record.id);

    const handleMerge = () => {
        if (!survivor) return;
        mergeLeadsMutation.mutate(
            { survivorId: survivor.id, mergedIds },
            {
                onSuccess: () => {
                    enqueueSnackbar(
                        `Merged ${mergedIds.length} lead${mergedIds.length !== 1 ? 's' : ''} into ${survivor.name}`,
                        { variant: 'success', autoHideDuration: 3000 }
                    );
                    onMerged?.({ survivor, mergedIds });
                },
                onError: (error: any) => {
                    console.error('Merge leads error:', error);
                    setConfirming(false);
                    enqueueSnackbar(errorMessage(error), {
                        variant: 'error',
                        autoHideDuration: 5000,
                    });
                },
            }
        );
    };

    /** Why a record that is not kept cannot be merged into the kept one. */
    const blockedReason = (record: DuplicateRecord) => {
        if (record.type === 'customer') return 'Customer, can only be kept';
        if (record.has_financial_records) return 'Has trading records, can only be kept';
        return 'No permission to merge';
    };

    return (
        <div className="rounded-lg border border-border">
            <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
                <div className="text-sm">
                    <span className="font-medium">{group.records.length} records</span>
                    <span className="text-muted-foreground"> · {matchLabel(group.matched_by)}</span>
                </div>
                {confirming ? (
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                            This cannot be undone.
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfirming(false)}
                            disabled={mergeLeadsMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleMerge}
                            disabled={mergeLeadsMutation.isPending}
                        >
                            {mergeLeadsMutation.isPending ? 'Merging...' : 'Confirm merge'}
                        </Button>
                    </div>
                ) : (
                    <Button
                        size="sm"
                        onClick={() => setConfirming(true)}
                        disabled={!survivor || mergedIds.length === 0}
                    >
                        <GitMerge className="h-4 w-4" />
                        {mergedIds.length > 0
                            ? `Merge ${mergedIds.length} into kept ${survivor?.type ?? 'record'}`
                            : 'Merge'}
                    </Button>
                )}
            </div>

            <RadioGroup
                value={survivorId ?? ''}
                onValueChange={value => {
                    setSurvivorId(value);
                    setConfirming(false);
                }}
                className="gap-0 divide-y divide-border"
            >
                {group.records.map(record => {
                    const isSurvivor = record.id === survivorId;
                    const included =
                        !isSurvivor && record.can_be_merged_in && !excluded.includes(record.id);

                    return (
                        <div
                            key={record.id}
                            className={`grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 px-4 py-3 text-sm ${
                                isSurvivor ? 'bg-primary/5' : ''
                            }`}
                        >
                            <RadioGroupItem
                                id={`keep-record-${record.id}`}
                                value={record.id}
                                className="mt-0.5"
                                disabled={!record.can_keep}
                                aria-label={`Keep ${record.name}`}
                            />
                            <div className="grid min-w-0 gap-x-6 gap-y-1 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)]">
                                <div className="min-w-0">
                                    <div className="flex min-w-0 items-center gap-2">
                                        <Link
                                            to={recordUrl(record)}
                                            target="_blank"
                                            className="truncate font-medium hover:underline"
                                        >
                                            {record.name}
                                        </Link>
                                        <span className="shrink-0 rounded border border-border px-1.5 text-[11px] leading-5 text-muted-foreground">
                                            {record.type === 'customer' ? 'Customer' : 'Lead'}
                                        </span>
                                        {record.id === currentId && (
                                            <span className="shrink-0 text-[11px] text-muted-foreground">
                                                (this page)
                                            </span>
                                        )}
                                    </div>
                                    <div className="truncate text-muted-foreground">
                                        {record.email}
                                    </div>
                                </div>
                                <div className="min-w-0 text-muted-foreground">
                                    <div className="truncate">{record.phone || '-'}</div>
                                    {record.additional_phone_number && (
                                        <div className="truncate">
                                            {record.additional_phone_number}
                                        </div>
                                    )}
                                </div>
                                <div className="min-w-0 space-y-1 text-muted-foreground">
                                    {record.status && (
                                        <StatusBadge
                                            status={record.status.name}
                                            kind="lead"
                                            label={record.status.label}
                                            color={record.status.color}
                                        />
                                    )}
                                    <div className="truncate">
                                        {record.assigned_user?.name || 'Unassigned'} ·{' '}
                                        <DateTimeFormatter date={record.created_at} />
                                    </div>
                                </div>
                            </div>
                            <div className="flex min-w-[7.5rem] items-center justify-end gap-2 pt-0.5">
                                {isSurvivor ? (
                                    <span className="text-xs font-medium text-primary">Kept</span>
                                ) : record.can_be_merged_in ? (
                                    <>
                                        <Checkbox
                                            id={`merge-record-${record.id}`}
                                            checked={included}
                                            onCheckedChange={checked => {
                                                setConfirming(false);
                                                setExcluded(current =>
                                                    checked
                                                        ? current.filter(id => id !== record.id)
                                                        : [...current, record.id]
                                                );
                                            }}
                                        />
                                        <Label
                                            htmlFor={`merge-record-${record.id}`}
                                            className="cursor-pointer text-xs font-normal"
                                        >
                                            Merge in
                                        </Label>
                                    </>
                                ) : (
                                    <span className="text-right text-xs text-muted-foreground">
                                        {blockedReason(record)}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </RadioGroup>
        </div>
    );
};

export default DuplicateGroupCard;
